const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

async function apiRequest(method: string, path: string, token: string, body?: any) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

// Users
export async function initializeUser(token: string, displayName: string) {
  return apiRequest('POST', '/api/initialize_user', token, { display_name: displayName });
}
export async function getMyProfile(token: string) {
  return apiRequest('GET', '/api/profile/me', token);
}
export async function updateMyProfile(token: string, updates: any) {
  return apiRequest('PUT', '/api/profile/me', token, updates);
}

// Documents
export async function uploadDocument(token: string, file: File, meta: { label?: string; doc_type?: string; jurisdiction?: string; tags?: string }) {
  const form = new FormData();
  form.append('file', file);
  if (meta.label) form.append('label', meta.label);
  if (meta.doc_type) form.append('doc_type', meta.doc_type);
  if (meta.jurisdiction) form.append('jurisdiction', meta.jurisdiction);
  if (meta.tags) form.append('tags', meta.tags);

  const res = await fetch(`${API_URL}/api/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
}
export async function listDocuments(token: string) {
  return apiRequest('GET', '/api/documents', token);
}
export async function getDocument(token: string, docId: string, includeText = false) {
  return apiRequest('GET', `/api/documents/${docId}?include_text=${includeText}`, token);
}
export async function deleteDocument(token: string, docId: string) {
  return apiRequest('DELETE', `/api/documents/${docId}`, token);
}
export async function searchDocuments(token: string, query: string) {
  return apiRequest('GET', `/api/documents/search?q=${encodeURIComponent(query)}`, token);
}
export async function updateDocument(token: string, docId: string, updates: any) {
  return apiRequest('PUT', `/api/documents/${docId}`, token, updates);
}

// PDF annotation
export function documentFileUrl(docId: string) {
  return `${API_URL}/api/documents/${docId}/file`;
}
export async function fetchDocumentBytes(token: string, docId: string): Promise<ArrayBuffer> {
  const res = await fetch(documentFileUrl(docId), { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Failed to fetch document (${res.status})`);
  return res.arrayBuffer();
}
export async function getDocumentCoords(token: string, docId: string, refresh = false) {
  return apiRequest('GET', `/api/documents/${docId}/coords${refresh ? '?refresh=1' : ''}`, token);
}
export async function listAnnotations(token: string, docId: string) {
  return apiRequest('GET', `/api/documents/${docId}/annotations`, token);
}
export async function createAnnotation(token: string, docId: string, body: { page_index: number; boxes: any[]; text?: string; comment?: string; color?: string }) {
  return apiRequest('POST', `/api/documents/${docId}/annotations`, token, body);
}
export async function updateAnnotation(token: string, docId: string, annoId: string, updates: { comment?: string; color?: string }) {
  return apiRequest('PUT', `/api/documents/${docId}/annotations/${annoId}`, token, updates);
}
export async function deleteAnnotation(token: string, docId: string, annoId: string) {
  return apiRequest('DELETE', `/api/documents/${docId}/annotations/${annoId}`, token);
}
export async function addReply(token: string, docId: string, annoId: string, text: string) {
  return apiRequest('POST', `/api/documents/${docId}/annotations/${annoId}/replies`, token, { text });
}
export async function deleteReply(token: string, docId: string, annoId: string, replyId: string) {
  return apiRequest('DELETE', `/api/documents/${docId}/annotations/${annoId}/replies/${replyId}`, token);
}

// Shares (read-only public links)
export async function createShare(token: string, docId: string) {
  return apiRequest('POST', `/api/documents/${docId}/share`, token, {});
}
export async function listShares(token: string, docId: string) {
  return apiRequest('GET', `/api/documents/${docId}/shares`, token);
}
export async function revokeShare(token: string, shareToken: string) {
  return apiRequest('POST', `/api/documents/share/${shareToken}/revoke`, token, {});
}
export async function getShared(shareToken: string) {
  const res = await fetch(`${API_URL}/api/share/${shareToken}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}
export function sharedFileUrl(shareToken: string) {
  return `${API_URL}/api/share/${shareToken}/file`;
}
export async function fetchSharedBytes(shareToken: string): Promise<ArrayBuffer> {
  const res = await fetch(sharedFileUrl(shareToken));
  if (!res.ok) throw new Error(`Failed to fetch document (${res.status})`);
  return res.arrayBuffer();
}

// Analysis
export async function getLegalEntities(token: string, docId: string, refresh = false) {
  return apiRequest('GET', `/api/analysis/entities/${docId}${refresh ? '?refresh=1' : ''}`, token);
}
export async function updateLegalEntities(token: string, docId: string, entities: Record<string, string[]>) {
  return apiRequest('PUT', `/api/analysis/entities/${docId}`, token, { entities });
}
export async function lookupCitation(token: string, category: string, q: string) {
  return apiRequest('GET', `/api/analysis/citations/lookup?category=${encodeURIComponent(category)}&q=${encodeURIComponent(q)}`, token);
}
export async function translateDocument(token: string, docId: string, to: 'en' | 'zh-Hans' | 'zh-Hant', refresh = false) {
  return apiRequest('POST', `/api/analysis/translate/${docId}`, token, { to, refresh });
}
export async function analyzeRisk(token: string, docId: string, jurisdiction = 'HK') {
  return apiRequest('POST', `/api/analysis/risk/${docId}`, token, { jurisdiction });
}
export async function askAboutDocument(token: string, question: string, docId?: string) {
  return apiRequest('POST', '/api/analysis/ask', token, { question, doc_id: docId });
}
export async function summarizeDocument(token: string, docId: string) {
  return apiRequest('POST', `/api/analysis/summarize/${docId}`, token);
}
export async function calculateLimitationPeriod(token: string, params: any) {
  return apiRequest('POST', '/api/analysis/limitation_period', token, params);
}
export async function exportAnalysis(token: string, docId: string) {
  return apiRequest('GET', `/api/analysis/export/${docId}`, token);
}
export async function compareDocuments(token: string, docIdA: string, docIdB: string) {
  return apiRequest('POST', '/api/analysis/compare', token, { doc_id_a: docIdA, doc_id_b: docIdB });
}

// Community
export async function getPersonas(token: string) {
  return apiRequest('GET', '/api/community/personas', token);
}
export async function askPersona(token: string, personaId: string, question: string, history?: { role: string; content: string }[]) {
  return apiRequest('POST', '/api/community/ask_persona', token, { persona_id: personaId, question, history });
}
export async function getThreads(token: string) {
  return apiRequest('GET', '/api/community/threads', token);
}
export async function getMyThreads(token: string) {
  return apiRequest('GET', '/api/community/threads/mine', token);
}
export async function saveThread(token: string, personaId: string, personaName: string, messages: any[]) {
  return apiRequest('POST', '/api/community/threads', token, { persona_id: personaId, persona_name: personaName, messages });
}

// Templates
export async function getTemplates(token: string) {
  return apiRequest('GET', '/api/templates', token);
}
export async function generateTemplate(token: string, params: any) {
  return apiRequest('POST', '/api/templates/generate', token, params);
}

// Analytics
export async function getAnalyticsOverview(token: string) {
  return apiRequest('GET', '/api/analytics/overview', token);
}

// Cases
export async function listCases(token: string) {
  return apiRequest('GET', '/api/cases', token);
}
export async function getCase(token: string, caseId: string) {
  return apiRequest('GET', `/api/cases/${caseId}`, token);
}
export async function createCase(token: string, data: any) {
  return apiRequest('POST', '/api/cases', token, data);
}
export async function updateCase(token: string, caseId: string, updates: any) {
  return apiRequest('PUT', `/api/cases/${caseId}`, token, updates);
}
export async function deleteCase(token: string, caseId: string) {
  return apiRequest('DELETE', `/api/cases/${caseId}`, token);
}
export async function addCaseEvent(token: string, caseId: string, event: any) {
  return apiRequest('POST', `/api/cases/${caseId}/events`, token, event);
}
